import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { SearchWorkflowTasksResponse } from "../../service/query";
import { searchWorkflowTasks } from "../../service/query/QueryService.full";
import { Status } from "../../service/Shared";
import { Toast } from "../../widget";

type DeepLinkedTask = SearchWorkflowTasksResponse["results"][number];

/**
 * Resolves the task named by a `?taskUuid=` query param, so a page hosting a task grid can open that
 * task's overlay on arrival. A notification links straight to a task (KAN-9609), and the task is read
 * by uuid rather than picked out of the grid, whose rows only cover the page currently loaded.
 *
 * The link is consumed once acted on, so the same notification can be opened again: were the param
 * left in place, a second click would navigate to an identical URL, leaving the uuid below unchanged
 * with nothing to re-run and no reopened panel.
 *
 * `enabled` lets a page that hosts more than one task view name a single owner of the link. Hooks
 * cannot be called conditionally, so every view's hook runs on every render: without this, each one
 * would resolve the same uuid, issuing duplicate requests and stacking a toast per view when the task
 * cannot be read. Passing false also drops any task already resolved, closing that view's overlay.
 */
export const useDeepLinkedTask = (enabled = true): DeepLinkedTask | undefined => {
  const [searchParams, setSearchParams] = useSearchParams();
  const taskUuid = searchParams.get("taskUuid");
  const [task, setTask] = useState<DeepLinkedTask>();

  /*
    react-router rebuilds setSearchParams whenever the query string changes, so it is held in a ref to
    keep the effect below keyed on the link alone. As a dependency it would re-resolve the task on any
    unrelated param change — a filter, a tab — reopening a panel the user had just closed.
  */
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    // The view that stood down (see `enabled`) holds no task, so its overlay closes with it.
    if (!enabled) {
      setTask(undefined);
      return undefined;
    }

    /*
      Nothing to resolve. The param is consumed below as the task is delivered, so its absence here is
      usually this hook's own doing rather than a link being taken away — what was delivered stays put,
      leaving the overlay open until its host closes it.
    */
    if (!taskUuid) {
      return undefined;
    }

    // A different link arrived: drop the previous one before the request starts.
    setTask(undefined);

    // Flipped by the cleanup below; a response for a superseded param is ignored.
    let isCurrent = true;

    /*
      Take the link out of the URL once it has been acted on, whether or not it resolved — a link that
      failed should retry on the next click rather than sit there. Replace rather than push, since a
      consumed link is not a step to go back to.
    */
    const consumeLink = (): void => {
      setSearchParamsRef.current(
        (previous) => {
          previous.delete("taskUuid");
          return previous;
        },
        { replace: true }
      );
    };

    const loadTask = async (): Promise<void> => {
      try {
        const response = await searchWorkflowTasks({
          paging: { afterCursor: null, beforeCursor: null, limit: 1 },
          filter: { results: { uuid: { operator: "eq", value: taskUuid } } },
        });

        if (!isCurrent) return;
        consumeLink();

        if (response.status === Status.Success && response.data?.results.length) {
          setTask(response.data.results[0]);
        } else {
          Toast.error({ message: "The task this link points to could not be opened." });
        }
      } catch {
        if (!isCurrent) return;
        consumeLink();
        Toast.error({ message: "The task this link points to could not be opened." });
      }
    };

    loadTask();

    return () => {
      isCurrent = false;
    };
  }, [taskUuid, enabled]);

  return task;
};
